import CustomTooltip from '../../../shared/CustomTooltip';
import {
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    Box,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
    useGetGuestUserProfileQuery,
    useGetProfileDetailsQuery,
    useGetTeamListQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import GuestPublicProfileRow from '../../Reviews/GuestPublicProfileRow';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import UserRole from '../../../../shared/AuthRoles';
import { normalizeProfilePhoneInput } from '../../../../shared/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import FormTextField from '../../../shared/FormTextField';
import FormSelect from '../../../shared/FormSelect';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import EditPasswordModal from './EditPasswordModal';
import { Edit } from '@mui/icons-material';
import { Department } from '../../../types/department';
import { AdminRole } from '../../../enums/users';
import ProfileSectionCard from '../ProfileSectionCard';
import ProfileReadOnlyField, {
    personalAccessLabelFromProfile,
    personalAccessTooltipContent,
    PROFILE_FIELD_NO_PERMISSION_MESSAGE,
    ProfileDisabledFieldWrap,
    ProfileShellFormCell,
    ProfileStatusDot,
    ProfileStatusIndicator,
} from '../ProfileReadOnlyField';
import { useEditProfileNavigation } from '../useEditProfileNavigation';
import { useProfileInlineUx } from '../ProfileInlineUxContext';

interface PersonalDetailsProps {
    isEditing: boolean;
    modernProfileView?: boolean;
    contractorCompany?: boolean;
    /** When omitted, defaults to `isEditing` (full edit page). */
    isEditingLoginFields?: boolean;
}

export default function PersonalDetailsComponent({
    isEditing,
    modernProfileView = false,
    contractorCompany = false,
    isEditingLoginFields: isEditingLoginFieldsProp,
}: PersonalDetailsProps) {
    const { control, reset, getValues } = useFormContext();
    const { isDirty } = useFormState({ control });
    const firstName = useWatch({ control, name: 'firstName' });
    const lastName = useWatch({ control, name: 'lastName' });
    const isActiveW = useWatch({ control, name: 'isActive' });
    const phoneW = useWatch({ control, name: 'phone' });
    const vehiclePlateW = useWatch({ control, name: 'vehiclePlate' });
    const departmentIdW = useWatch({ control, name: 'department.id' });
    const loginEmailW = useWatch({ control, name: 'email' });
    const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false);

    const loginFieldsActive =
        isEditingLoginFieldsProp !== undefined
            ? isEditingLoginFieldsProp
            : isEditing;

    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const loggedInUser = UserRole();
    const { data: profileDetails, isFetching } =
        useGetProfileDetailsQuery('/profile');
    const isGuestUser = Boolean(loggedInUser?.role?.includes('guest'));
    const { data: guestUserProfile } = useGetGuestUserProfileQuery(
        loggedInUser?.sub,
        {
            skip: !isGuestUser || !loggedInUser?.sub,
        },
    );

    const { data: getTeams } = useGetTeamListQuery(loggedInUser?.company);

    const { canShowEdit } = useEditProfileNavigation(contractorCompany);
    const {
        suspendProfileResets,
        inlineEditSection,
        beginInlineEdit,
        cancelInlineEdit,
        saveInlineEdit,
    } = useProfileInlineUx();

    useEffect(() => {
        if (!profileDetails) return;
        if (suspendProfileResets) return;

        reset(
            {
                ...getValues(),
                ...profileDetails,
                phone: normalizeProfilePhoneInput(
                    (profileDetails as { phone?: string }).phone ??
                        (getValues() as { phone?: string }).phone,
                ),
            },
            { keepDirtyValues: true },
        );
    }, [profileDetails, reset, getValues, suspendProfileResets]);

    const [teams, setTeams] = useState<any[]>([]);

    useEffect(() => {
        if (!getTeams) return;

        const allTeams = getTeams.map((val: Department) => ({
            value: val.id,
            label: val?.name?.charAt(0)?.toUpperCase() + val?.name.slice(1),
        }));

        const filterOutDuplicates = allTeams.filter(
            (value: any, index: any, self: any) =>
                index ===
                self.findIndex(
                    (t: any) =>
                        t?.label === value?.label && t?.label === value?.label,
                ),
        );

        setTeams(filterOutDuplicates);
    }, [getTeams]);

    const shell = Boolean(modernProfileView);
    const readOnlyPersonalShell = shell && !isEditing;
    const readOnlyLoginShell = shell && !loginFieldsActive;

    const teamLabel = useMemo(() => {
        if (departmentIdW == null) return '';
        const t = teams.find(
            (x: { value: number }) => x.value === departmentIdW,
        );
        return t?.label ?? '';
    }, [departmentIdW, teams]);

    const renderPersonalDetailsGrid = () => (
        <Grid container columnSpacing='50px' rowSpacing='10px'>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>First Name</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='firstName'
                        control={control}
                        disabled={!isEditing}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Surname</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='lastName'
                        control={control}
                        disabled={!isEditing}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <Stack direction='row' gap='5px' alignItems='center'>
                        <InputLabel>Status</InputLabel>
                        <CustomTooltip
                            title={
                                <Typography>
                                    <List>
                                        <ListItem>
                                            {' '}
                                            Enabled: Users can log in and use
                                            the platform according to their
                                            assigned permissions.
                                        </ListItem>
                                        <ListItem>
                                            {' '}
                                            Disabled: Users cannot log in or
                                            access the platform.
                                        </ListItem>
                                    </List>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    </Stack>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <CustomTooltip
                        title={
                            'You do not have permission to change this. Please contact your administrator.'
                        }
                    >
                        <div>
                            <FormSelect
                                name='isActive'
                                control={control}
                                disabled
                                options={[
                                    {
                                        value: true,
                                        label: 'Enabled',
                                        iconLeft: <ProfileStatusDot enabled />,
                                    },
                                    {
                                        value: false,
                                        label: 'Disabled',
                                        iconLeft: (
                                            <ProfileStatusDot enabled={false} />
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    </CustomTooltip>
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <Stack direction='row' gap='5px' alignItems='center'>
                        <InputLabel>Access</InputLabel>
                        <CustomTooltip
                            title={personalAccessTooltipContent(loggedInUser)}
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    </Stack>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <CustomTooltip
                        title={
                            'You do not have permission to change this. Please contact your administrator.'
                        }
                    >
                        <div>
                            <TextField
                                size='small'
                                fullWidth
                                value={
                                    profileDetails?.role === 'owner_admin'
                                        ? 'Admin'
                                        : profileDetails?.role === 'owner_user'
                                          ? 'User'
                                          : profileDetails?.role ===
                                              'guest_department_admin'
                                            ? 'Team Admin'
                                            : profileDetails?.role ===
                                                'external_attendee_user'
                                              ? 'External Attendee'
                                              : profileDetails?.role ===
                                                  'guest_user'
                                                ? profileDetails?.viewOnly
                                                    ? 'View Only User'
                                                    : 'User'
                                                : 'Company Admin'
                                }
                                disabled
                            />
                        </div>
                    </CustomTooltip>
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Phone</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='phone'
                        control={control}
                        disabled={!isEditing}
                        digitsOnlyMaxLength={10}
                    />
                </Grid>
            </Grid>
            {loggedInUser?.role.includes('guest') && (
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel>Team</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <CustomTooltip
                            title={
                                loggedInUser?.role === 'guest_user'
                                    ? `You do not have
                                                    permission to change
                                                    your Team. Please
                                                    contact your company
                                                    admin or Team admin to
                                                    make these changes.`
                                    : ''
                            }
                        >
                            <div>
                                <FormAutocomplete
                                    name='department.id'
                                    control={control}
                                    options={teams}
                                    disabled={
                                        !isEditing ||
                                        !loggedInUser?.role.includes('admin')
                                    }
                                    disableClearable={undefined}
                                />
                            </div>
                        </CustomTooltip>
                    </Grid>
                </Grid>
            )}

            {loggedInUser?.role !== AdminRole.Admin && (
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <Stack direction='row' gap='5px' alignItems='center'>
                            <InputLabel>Vehicle Plate</InputLabel>
                            <CustomTooltip
                                title={
                                    <Typography>
                                        Some experiences may require your
                                        vehicle’s plate number to allow access
                                        to the location.
                                    </Typography>
                                }
                                placement='right'
                            >
                                <HelpOutlineIcon
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '16px',
                                    }}
                                />
                            </CustomTooltip>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <FormTextField
                            name='vehiclePlate'
                            control={control}
                            disabled={!isEditing}
                        />
                    </Grid>
                </Grid>
            )}
        </Grid>
    );

    const renderLoginDetailsGrid = () => (
        <Grid
            container
            columnSpacing='50px'
            rowSpacing='10px'
            sx={{ paddingBottom: shell ? 0 : '50px' }}
        >
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel>Email</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='email'
                        control={control}
                        disabled={
                            !loginFieldsActive ||
                            !loggedInUser?.role.includes('admin')
                        }
                    />
                </Grid>
            </Grid>
            {loggedInUser?.role !== 'external_attendee_user' && (
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel>Password</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7} sx={{ position: 'relative' }}>
                        <TextField
                            fullWidth
                            size='small'
                            value={'********'}
                            disabled
                        />
                        {(!shell || loginFieldsActive) && (
                            <IconButton
                                onClick={() => setPasswordModalOpen(true)}
                                sx={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    '&:hover': {
                                        color: 'primary.main',
                                    },
                                }}
                            >
                                <Edit fontSize='small' />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
            )}
        </Grid>
    );

    const renderPersonalReadOnlyGrid = () => (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='First name'>
                    {firstName}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Surname'>
                    {lastName}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField
                    label='Status'
                    labelAdornment={
                        <CustomTooltip
                            title={
                                <Typography>
                                    <List>
                                        <ListItem>
                                            Enabled: Users can log in and use
                                            the platform according to their
                                            assigned permissions.
                                        </ListItem>
                                        <ListItem>
                                            Disabled: Users cannot log in or
                                            access the platform.
                                        </ListItem>
                                    </List>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    }
                >
                    <ProfileStatusIndicator active={isActiveW} />
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField
                    label='Access'
                    labelAdornment={
                        <CustomTooltip
                            title={personalAccessTooltipContent(loggedInUser)}
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{ color: 'text.primary', fontSize: '16px' }}
                            />
                        </CustomTooltip>
                    }
                >
                    {personalAccessLabelFromProfile(profileDetails)}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Phone'>
                    {phoneW}
                </ProfileReadOnlyField>
            </Grid>
            {loggedInUser?.role.includes('guest') && (
                <Grid item xs={12} sm={6} md={4}>
                    <ProfileReadOnlyField
                        label='Team'
                        {...(loggedInUser?.role === 'guest_user'
                            ? {
                                  labelAdornment: (
                                      <CustomTooltip
                                          title='You do not have permission to change your Team. Please contact your company admin or Team admin to make these changes.'
                                          placement='right'
                                      >
                                          <HelpOutlineIcon
                                              sx={{
                                                  color: 'text.primary',
                                                  fontSize: '16px',
                                              }}
                                          />
                                      </CustomTooltip>
                                  ),
                              }
                            : {})}
                    >
                        {teamLabel}
                    </ProfileReadOnlyField>
                </Grid>
            )}
            {loggedInUser?.role !== AdminRole.Admin && (
                <Grid item xs={12} sm={6} md={4}>
                    <ProfileReadOnlyField
                        label='Vehicle plate'
                        labelAdornment={
                            <CustomTooltip
                                title={
                                    <Typography>
                                        Some experiences may require your
                                        vehicle&apos;s plate number to allow
                                        access to the location.
                                    </Typography>
                                }
                                placement='right'
                            >
                                <HelpOutlineIcon
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '16px',
                                    }}
                                />
                            </CustomTooltip>
                        }
                    >
                        {vehiclePlateW}
                    </ProfileReadOnlyField>
                </Grid>
            )}
        </Grid>
    );

    const renderLoginReadOnlyGrid = () => (
        <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ paddingBottom: shell ? 0 : '50px' }}
        >
            <Grid item xs={12} md={6}>
                <ProfileReadOnlyField label='Email'>
                    {loginEmailW}
                </ProfileReadOnlyField>
            </Grid>
            {loggedInUser?.role !== 'external_attendee_user' && (
                <Grid item xs={12} md={6}>
                    <ProfileReadOnlyField label='Password'>
                        ••••••••
                    </ProfileReadOnlyField>
                </Grid>
            )}
        </Grid>
    );

    const renderPersonalShellEditGrid = () => (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='First name'>
                    <ProfileDisabledFieldWrap disabled={!isEditing}>
                        <FormTextField
                            name='firstName'
                            control={control}
                            disabled={!isEditing}
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Surname'>
                    <ProfileDisabledFieldWrap disabled={!isEditing}>
                        <FormTextField
                            name='lastName'
                            control={control}
                            disabled={!isEditing}
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell
                    label='Status'
                    labelAdornment={
                        <CustomTooltip
                            title={
                                <Typography>
                                    <List>
                                        <ListItem>
                                            Enabled: Users can log in and use
                                            the platform according to their
                                            assigned permissions.
                                        </ListItem>
                                        <ListItem>
                                            Disabled: Users cannot log in or
                                            access the platform.
                                        </ListItem>
                                    </List>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    }
                >
                    <ProfileDisabledFieldWrap disabled>
                        <FormSelect
                            name='isActive'
                            control={control}
                            disabled
                            size='small'
                            fullWidth
                            options={[
                                {
                                    value: true,
                                    label: 'Enabled',
                                    iconLeft: <ProfileStatusDot enabled />,
                                },
                                {
                                    value: false,
                                    label: 'Disabled',
                                    iconLeft: (
                                        <ProfileStatusDot enabled={false} />
                                    ),
                                },
                            ]}
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell
                    label='Access'
                    labelAdornment={
                        <CustomTooltip
                            title={personalAccessTooltipContent(loggedInUser)}
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{ color: 'text.primary', fontSize: '16px' }}
                            />
                        </CustomTooltip>
                    }
                >
                    <TextField
                        size='small'
                        fullWidth
                        disabled
                        value={personalAccessLabelFromProfile(profileDetails)}
                    />
                </ProfileShellFormCell>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileShellFormCell label='Phone'>
                    <ProfileDisabledFieldWrap disabled={!isEditing}>
                        <FormTextField
                            name='phone'
                            control={control}
                            disabled={!isEditing}
                            digitsOnlyMaxLength={10}
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            {loggedInUser?.role.includes('guest') && (
                <Grid item xs={12} sm={6} md={4}>
                    <ProfileShellFormCell label='Team'>
                        <ProfileDisabledFieldWrap
                            disabled={
                                !isEditing ||
                                !loggedInUser?.role.includes('admin')
                            }
                            title={
                                loggedInUser?.role === 'guest_user'
                                    ? 'You do not have permission to change your Team. Please contact your company admin or Team admin to make these changes.'
                                    : PROFILE_FIELD_NO_PERMISSION_MESSAGE
                            }
                        >
                            <FormAutocomplete
                                name='department.id'
                                control={control}
                                options={teams}
                                disabled={
                                    !isEditing ||
                                    !loggedInUser?.role.includes('admin')
                                }
                                disableClearable={undefined}
                            />
                        </ProfileDisabledFieldWrap>
                    </ProfileShellFormCell>
                </Grid>
            )}
            {loggedInUser?.role !== AdminRole.Admin && (
                <Grid item xs={12} sm={6} md={4}>
                    <ProfileShellFormCell
                        label='Vehicle plate'
                        labelAdornment={
                            <CustomTooltip
                                title={
                                    <Typography>
                                        Some experiences may require your
                                        vehicle&apos;s plate number to allow
                                        access to the location.
                                    </Typography>
                                }
                                placement='right'
                            >
                                <HelpOutlineIcon
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '16px',
                                    }}
                                />
                            </CustomTooltip>
                        }
                    >
                        <ProfileDisabledFieldWrap disabled={!isEditing}>
                            <FormTextField
                                name='vehiclePlate'
                                control={control}
                                disabled={!isEditing}
                                size='small'
                                fullWidth
                            />
                        </ProfileDisabledFieldWrap>
                    </ProfileShellFormCell>
                </Grid>
            )}
        </Grid>
    );

    const renderLoginShellEditGrid = () => (
        <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ paddingBottom: shell ? 0 : '50px' }}
        >
            <Grid item xs={12} md={6}>
                <ProfileShellFormCell label='Email'>
                    <ProfileDisabledFieldWrap
                        disabled={
                            !loginFieldsActive ||
                            !loggedInUser?.role.includes('admin')
                        }
                    >
                        <FormTextField
                            name='email'
                            control={control}
                            disabled={
                                !loginFieldsActive ||
                                !loggedInUser?.role.includes('admin')
                            }
                            size='small'
                            fullWidth
                        />
                    </ProfileDisabledFieldWrap>
                </ProfileShellFormCell>
            </Grid>
            {loggedInUser?.role !== 'external_attendee_user' && (
                <Grid item xs={12} md={6}>
                    <ProfileShellFormCell label='Password'>
                        <Box sx={{ position: 'relative' }}>
                            <TextField
                                fullWidth
                                size='small'
                                value='********'
                                disabled
                            />
                            {(!shell || loginFieldsActive) && (
                                <IconButton
                                    onClick={() => setPasswordModalOpen(true)}
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        '&:hover': {
                                            color: 'primary.main',
                                        },
                                    }}
                                >
                                    <Edit fontSize='small' />
                                </IconButton>
                            )}
                        </Box>
                    </ProfileShellFormCell>
                </Grid>
            )}
        </Grid>
    );

    return (
        <>
            {isFetching === true ? (
                <Grid
                    sx={{
                        height: isIpad ? '50vh' : isMobile ? '5px' : '50vh',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        alignContent: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <LoadingComponent />
                </Grid>
            ) : shell ? (
                <>
                    <ProfileSectionCard
                        icon={<PersonIcon sx={{ fontSize: 22 }} />}
                        title='Personal Details'
                        subtitle='Your personal information and role.'
                        editButtonLabel='Edit Personal Details'
                        onEdit={() => beginInlineEdit('personal')}
                        showEditButton={canShowEdit}
                        inlineEditActive={inlineEditSection === 'personal'}
                        onCancelInline={cancelInlineEdit}
                        onSaveInline={saveInlineEdit}
                        saveDisabled={
                            inlineEditSection === 'personal' && !isDirty
                        }
                    >
                        {isGuestUser && loggedInUser?.sub ? (
                            <GuestPublicProfileRow
                                averageRating={guestUserProfile?.averageRating}
                                reviewCount={guestUserProfile?.reviewCount}
                                to={`/guest-users/${loggedInUser.sub}`}
                            />
                        ) : null}
                        {readOnlyPersonalShell
                            ? renderPersonalReadOnlyGrid()
                            : renderPersonalShellEditGrid()}
                    </ProfileSectionCard>
                    <ProfileSectionCard
                        icon={<ShieldOutlinedIcon sx={{ fontSize: 22 }} />}
                        title='Login & Security'
                        subtitle='Manage your login email and password.'
                        editButtonLabel='Edit Login & Security'
                        onEdit={() => beginInlineEdit('login')}
                        showEditButton={canShowEdit}
                        inlineEditActive={inlineEditSection === 'login'}
                        onCancelInline={cancelInlineEdit}
                        onSaveInline={saveInlineEdit}
                        saveDisabled={inlineEditSection === 'login' && !isDirty}
                    >
                        {readOnlyLoginShell
                            ? renderLoginReadOnlyGrid()
                            : renderLoginShellEditGrid()}
                    </ProfileSectionCard>
                </>
            ) : (
                <>
                    <Typography
                        component='h4'
                        sx={{
                            color: 'text.primary',
                            fontWeight: 'bold',
                            py: '30px',
                        }}
                    >
                        Personal Details
                    </Typography>
                    {renderPersonalDetailsGrid()}
                    <Typography
                        component='h4'
                        sx={{
                            color: 'text.primary',
                            fontWeight: 'bold',
                            py: '30px',
                        }}
                    >
                        Login Details
                    </Typography>
                    {renderLoginDetailsGrid()}
                </>
            )}
            <EditPasswordModal
                open={passwordModalOpen}
                onClose={() => setPasswordModalOpen(false)}
            />
        </>
    );
}
